import {Box, createStyles, Group, Stack, Text} from '@mantine/core';
import React from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {ProteinCreationSettingsData} from 'antibodies/nested-data-creation/full-screen/ProteinCreationSettingsUtil';
import useProteinCreationTableState from 'antibodies/nested-data-creation/full-screen/useProteinCreationTableState';
import {
  NdcSpreadsheetComponentFormatType,
  NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import Antibody from 'antibodies/visualization/Antibody';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

const TABLE_CID = 'protein-creation-table';
const GLYPH_SIZE = 48;

const useStyles = createStyles((theme) => ({
  headerContainer: {
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderTopLeftRadius: theme.radius.md,
    borderTopRightRadius: theme.radius.md,
    backgroundColor: theme.white,
  },
  glyphWrapper: {
    width: GLYPH_SIZE,
    height: GLYPH_SIZE,
    flexShrink: 0,
  },
}));

function TableHeader({
  format,
  startingType,
}: {
  format: AntibodyFormat;
  startingType: NdcSpreadsheetComponentFormatType;
}): JSX.Element {
  const {classes} = useStyles();
  const startingTypeLabel = NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL[startingType];

  return (
    <Group
      position="apart"
      noWrap={true}
      px="lg"
      py="sm"
      mx="10px"
      mb="-12px"
      className={classes.headerContainer}
    >
      <Group spacing="md" noWrap={true}>
        <Box className={classes.glyphWrapper}>
          <Antibody format={format} />
        </Box>
        <Stack spacing={2}>
          <Text size="md" weight={600}>
            {format.name} {startingTypeLabel}
          </Text>
          <Text size="xs" c="dimmed" lh="16px">
            Fill each column with an existing Benchling entity or a new DNA or AA sequence. Type or paste
            directly into cells, or pull sequences in from search
          </Text>
        </Stack>
      </Group>
    </Group>
  );
}

interface ProteinCreationTableProps {
  creationSettings: ProteinCreationSettingsData;
}

export default function ProteinCreationTable({creationSettings}: ProteinCreationTableProps): JSX.Element {
  const tableState = useProteinCreationTableState(creationSettings);

  return (
    <Stack spacing={0}>
      <TableHeader
        format={creationSettings.selectedFormat}
        startingType={creationSettings.selectedStartingType}
      />
      {tableState != null ? (
        <StructuredTableOutsideNotebook
          structuredTable={tableState.structuredTable}
          cid={TABLE_CID}
          updateHandler={tableState.handleTableUpdate}
          enableColumnResizing={true}
          includeUnsubmittedStatus={false}
          hideColumnHeaderIcons={true}
          canRename={false}
          suppressTitleBar={true}
        />
      ) : (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load protein creation table" />
      )}
    </Stack>
  );
}
